import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import TsGrid from "../../components/tsGrid";
import { generateRandomNumber } from "../../utils/function";
import useApi from "../../hooks/useApi";
import targetConfig from "../../api/targetConfigApi";
import { toast } from "react-toastify";
import { Modal, Tooltip } from "antd";
import { DeleteOutlined } from '@ant-design/icons';
import ProductDropdown from "../Performance Dashboard/productsDropdown";
import { MyContext } from "../../context/myContext";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import AgDropdownEditor from "./AgDropdownEditor";
import NumericInputEditor from "./AgNumericEditor";

export const generateRandomId = () => {
    const randomStr = generateRandomNumber().toString(36).substring(2, 15);
    const timestamp = new Date().getTime();
    const randomID = `newrec_${randomStr}_${timestamp}`;
    return randomID;
};

const TargetConfig: React.FC<any> = () => {
    const gridRef = useRef<any>(null);
    const gridApiRef = useRef<any>(null);
    const containerStyle = useMemo(() => ({ width: '100%', marginBottom: '10px' }), []);
    const gridStyle = useMemo(() => ({ width: '100%', padding: "0px !important" }), []);
    const [rowData, setRowData] = useState<any[]>([]);

    const getTargetsApi = useApi(targetConfig.getTargetsList);
    const updateTargetApi = useApi(targetConfig.UpdateTarget);
    const deleteTargetApi = useApi(targetConfig.DeleteTarget)

    const [workItemTypes, setWorkItemTypes] = useState<string[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [units, setUnits] = useState<string[]>([]);
    const [metricToUnitsMap, setMetricToUnitsMap] = useState<Record<string, string>>({});
    const { selectedProduct } = useContext<any>(MyContext);
    const saveTimeouts: { [rowId: string]: NodeJS.Timeout } = {};
    const [pinnedBottomRow, setPinnedBottomRow] = useState<any>({ id: generateRandomId(), isNewRow: true, product: selectedProduct });

    const getTargets = async () => {
        try {
            const targets = await getTargetsApi.request(selectedProduct);

            if (targets?.data) {
                const allWorkItemTypes = new Set<string>();
                const allCategories = new Set<string>();
                const allUnits = new Set<string>();

                const metricToUnitsMap: Record<string, string> = {};

                const list: any[] = [];

                targets.data.forEach((metric: any) => {
                    const metricName = metric.name;

                    if (metric.data.length > 0 && metric.data[0].datasets.length > 0) {
                        metricToUnitsMap[metricName] = metric.data[0].datasets[0].units;
                    }

                    metric.data.forEach((item: any) => {
                        const workItemType = item.work_item_type;

                        if (workItemType !== null || workItemType !== "undefined") {
                            allWorkItemTypes.add(workItemType);
                        }

                        item.datasets.forEach((dataset: any) => {
                            const category = dataset.category;
                            const units = dataset.units;

                            if (category  !== null || category !== "undefined") {
                                allCategories.add(category);
                            }

                            if (units) {
                                allUnits.add(units);
                            }

                            const rowId = dataset.id;

                            list.push({
                                id: rowId,
                                metric_name: metricName,
                                work_item_type: workItemType,
                                category: category,
                                target: dataset.target,
                                units: dataset.units,
                                effective_date: dataset.effective_date || null,
                                product: selectedProduct
                            });
                        });
                    });
                });

                setWorkItemTypes(Array.from(allWorkItemTypes));
                setCategories(Array.from(allCategories));
                setUnits(Array.from(allUnits));

                setMetricToUnitsMap(metricToUnitsMap);

                // Set only the actual data rows
                setRowData([...list]);
                // Update the pinned bottom row
                setPinnedBottomRow({ id: generateRandomId(), isNewRow: true, product: selectedProduct });
            }
        } catch (err) {
            toast.error('Failed to fetch target configuration data', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        }
    };

    useEffect(() => {
        getTargets();
    }, [selectedProduct]);

    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellClass: 'border-right',
            filter: true,
            suppressCellSelection: true,
            flex: 1
        };
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const onGroupExpanded = useCallback((event: any) => {
        if (event.node.expanded) {
            const currentGroupKey = event.node.key;
            
            gridApiRef.current.forEachNode((node: any) => {
                if (node.group &&
                    node.key !== currentGroupKey &&
                    node.expanded) {
                    node.setExpanded(false);
                }
            });
        }
    }, []);

    const addNewRow = useCallback(() => {
        const newPinnedRow = { id: generateRandomId(), isNewRow: true, product: selectedProduct };
        setPinnedBottomRow(newPinnedRow);
    }, [selectedProduct]);

    const ActionButton = (props: any) => {
        const handleOk = async () => {
            try {
                const rowId = props.data.id;
                const metricName = props.data?.metric_name;
                const idToSend = (typeof rowId === 'string' && rowId.startsWith('newrec_')) ? 0 : rowId;

                const payload = { id: idToSend };

                await deleteTargetApi.request(
                    selectedProduct,
                    { data: payload },
                    metricName
                );

                setRowData(prevData => {
                    const newData = prevData.filter(row => row.id !== rowId);
                    
                    requestAnimationFrame(() => {
                        if (gridApiRef.current) {
                          gridApiRef.current.forEachNode((node: any) => {
                            if (node.group && node.key === metricName) {
                              node.setExpanded(true);
                            }
                          });
                        }
                      });
                    
                    return newData;
                });

            } catch (err) {
                console.error("Delete API error:", err);
                toast.error('Failed to delete target configuration', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            } finally {
                if (props.data) {
                    props.data._isBeingDeleted = false;
                }
            }
        };

        const handleDeleteAction = () => {
            if (props.data) {
                props.data._isBeingDeleted = true;
            }
            
            Modal.confirm({
                title: `Are you sure you want to delete this target configuration?`,
                content: "This action cannot be undone.",
                okText: "Yes",
                cancelText: "No",
                onOk: () => {
                    handleOk();
                },
                onCancel: () => {
                    if (props.data) {
                        props.data._isBeingDeleted = false;
                    }
                }
            });
        };

        return (
            <div>
                {!props.data.isNewRow && (
                    <div className="flex gap-2 justify-center">
                        <Tooltip title={'Delete'}>
                            <DeleteOutlined
                                onClick={() => handleDeleteAction()}
                                style={{ color: '#000000', fontSize: '16px', cursor: 'pointer', marginTop:'16px' }}
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    };

    const formatDateToYYYYMMDD = (dateValue: any): string | null => {
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
        }

        if (!dateValue) {
            return null;
        }

        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

        if (isNaN(date.getTime())) {
            return null;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const saveData = async (params: any) => {
        const data = params?.data;
        try {
            if (!data || data.target === null || data.target === undefined) {
                return;
            }
    
            let idToSend = data.id;
            if (typeof idToSend === 'string' && idToSend.startsWith('newrec_')) {
                idToSend = 0;
            }
    
            const payload = {
                id: idToSend,
                name: data.metric_name,
                work_item_type: data.work_item_type,
                category: data.category,
                target: data.target,
                effective_date: formatDateToYYYYMMDD(data.effective_date),
            };

            const response = await updateTargetApi.request(selectedProduct, payload, data.metric_name);
    
            if (response?.data) {
                const newId = response.data.data[0]?.id;
                const updatedRow = {
                    ...data,
                    id: newId,
                    isNewRow: false,
                    units: metricToUnitsMap[data.metric_name] || data.units,
                    product: selectedProduct
                };
    
                setRowData(prevData => {
                    const newData = typeof data.id === 'string' && data.id.startsWith('newrec_')
                        ? [...prevData, updatedRow]
                        : prevData.map(row => row.id === data.id ? updatedRow : row);
                    
                        requestAnimationFrame(() => {
                        if (gridApiRef.current) {
                            gridApiRef.current.forEachNode((node: any) => {
                                if (node.group && node.key === data.metric_name) {
                                    node.setExpanded(true);
                                }
                            });
                        }
                    });
                    
                    return newData;
                });

                setPinnedBottomRow({ id: generateRandomId(), isNewRow: true, product: selectedProduct });
            }
        } catch (err) {
            toast.error('Failed to save target configuration', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        }
    };


    const onHandleCellValueChanged = useCallback((params: any) => {
        const data = params.data;

        if (data._isBeingDeleted) {
            return;
        }

        if (params.colDef.field === 'metric_name' && metricToUnitsMap[params.newValue]) {
            const newUnits = metricToUnitsMap[params.newValue];
            params.node.setDataValue('units', newUnits);
        }

        if (params.colDef.field === 'target' && (params.newValue === '' || params.newValue == null)) {
            params.node.setDataValue('target', null);
        }

        if (data.isNewRow && data.metric_name && data.target) {
            data.isNewRow = false;
            data.units = metricToUnitsMap[data.metric_name];
        }

        const rowId = data.id || params.node.rowIndex;
        if (saveTimeouts[rowId]) {
            clearTimeout(saveTimeouts[rowId]);
        }

        saveTimeouts[rowId] = setTimeout(() => {
            saveData(params);
            delete saveTimeouts[rowId];
        }, 500);
    }, [addNewRow, metricToUnitsMap]);

    const getMetricNames = useMemo(() => {
        const metricNames = Object.keys(metricToUnitsMap);
        return metricNames;
    }, [metricToUnitsMap]);

    const columnDefs = useMemo(() => [
        {
            headerName: 'Metric Name',
            field: 'metric_name',
            rowGroup: true,
            hide: true,
            rowGroupIndex: 0,
            valueGetter: (params: { data: { metric_name: string; isNewRow: boolean; }; }) => {
                if (params.data?.isNewRow || !params.data?.metric_name?.trim()) {
                    return null;
                }
                return params.data.metric_name.trim();
            },
            keyCreator: (params: { value: string; }) => {
                const val = params.value?.trim();
                return val || null;
            }
        },
        {
            headerName: 'Metric Name',
            field: 'metric_name',
            editable: (params: { data: { isNewRow?: boolean } }) => params.data?.isNewRow === true,
            cellEditor: AgDropdownEditor,
            cellEditorParams: (params: { value: string }): { value: string; options: Array<{ label: string; value: string }>; placeholder: string } => ({
                value: params.value,
                options: getMetricNames.map((name: string) => ({ label: name, value: name })),
                placeholder: 'Select Metric Name',
            }),
            minWidth: 180,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            cellStyle: (params: any) => { 
                return params.data?.isNewRow ? { backgroundColor: '#f0f8ff' } : null;
            },
            valueGetter: (params: any) => {
                if (params.data?.isNewRow) {
                    return params.data.metric_name;
                }
                return params.node.group ? params.data?.metric_name : '';
            }
        },
        {
            headerName: 'Work Item Type',
            field: 'work_item_type',
            editable: (params: { data: { isNewRow?: boolean } }) => params.data?.isNewRow === true,
            cellEditor: AgDropdownEditor,
            cellEditorParams: (params: any) => ({
                value: params.value,
                options: workItemTypes.map((c: string) => ({ label: c, value: c })),
                placeholder: 'Select Work Item Type',
              }),
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            minWidth: 150,
            cellStyle: (params: any) => {
                return params.data.isNewRow ? { backgroundColor: '#f0f8ff' } : null;
            }
        },
        {
            headerName: 'Category',
            field: 'category',
            editable: (params: { data: { isNewRow?: boolean } }) => params.data?.isNewRow === true,
            cellEditor: AgDropdownEditor,
            cellEditorParams: (params: any) => ({
                value: params.value,
                options: categories.map((c: string) => ({ label: c, value: c })),
                placeholder: 'Select category',
              }),
            minWidth: 150,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            cellStyle: (params: any) => {
                return params.data.isNewRow ? { backgroundColor: '#f0f8ff' } : null;
            }
        },
        {
            headerName: 'Target',
            field: 'target',
            editable: true,
            cellEditor: NumericInputEditor,
            minWidth: 120,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            cellStyle: (params: any) => {
                const isNew = params.data?.isNewRow;
                const isMissing = params.data?.metric_name && (params.data?.target === '' || params.data?.target == null);
                if (isNew && isMissing) {
                    return { backgroundColor: '#f0f8ff', border: '1px solid red' };
                }
                return isNew ? { backgroundColor: '#f0f8ff' } : null;
            },
            onCellValueChanged: (params: any) => {
                const value = params.newValue;
                if (value === '' || value == null || isNaN(Number(value))) {
                    params.node.setDataValue('target', null);
                }
            }
        },
        {
            headerName: 'Units',
            field: 'units',
            editable: false,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: (params: any) => ({
                values: units
            }),
            minWidth: 120,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            cellStyle: (params: { data: { isNewRow?: boolean } }) => {
                return params.data.isNewRow ? { backgroundColor: '#f0f8ff' } : null;
            }
        },
        {
            headerName: 'Effective Date',
            field: 'effective_date',
            editable: true,
            cellEditor: 'agDateCellEditor',
            valueFormatter: (params: any) => {
                if (!params.value) return '';
                const date = params.value instanceof Date ? params.value : new Date(params.value);
                if (!isNaN(date.getTime())) {
                    return formatDateToYYYYMMDD(date);
                }
                return params.value;
            },
            valueGetter: (params: any) => {
                return params.data?.effective_date;
            },
            minWidth: 150,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            cellStyle: (params: any) => {
                return params.data?.isNewRow ? { backgroundColor: '#f0f8ff' } : null;
            }
        },
        {
            field: "action",
            headerName: "",
            editable: false,
            sortable: false,
            filter: false,
            show: true,
            maxWidth: 60,
            minWidth: 60,
            resizable: false,
            cellRenderer: ActionButton,
            cellStyle: () => { return { cursor: "pointer" } },
        },
    ], [getMetricNames]);

    return (
        <div className="p-10">
            <div className="flex flex-col gap-4 mb-6">
                <h1 className="page-heading">Performance Target Configuration</h1>
                <div className='w-[200px]'>
                    <ProductDropdown />
                </div>
            </div>

            <div style={containerStyle}>
                <div className={`ag-theme-alpine grid_style mt-4`}>
                    <TsGrid
                        rowData={rowData}
                        columnDefs={columnDefs}
                        onGridReady={onGridReady}
                        gridRef={gridRef}
                        gridStyle={gridStyle}
                        defaultColDef={defaultColDef}
                        onCellValueChanged={onHandleCellValueChanged}
                        editType={"fullRow"}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        pagination={false}
                        modules={[RowGroupingModule]}
                        groupDisplayType="groupRows"
                        groupDefaultExpanded={0}
                        groupSuppressBlankHeader={true}
                        onGroupExpanded={onGroupExpanded}
                        onRowGroupOpened={onGroupExpanded}
                        autoGroupColumnDef={{
                            headerName: 'Metric Name',
                            minWidth: 180,
                            cellRendererParams: {
                                suppressCount: true
                            }
                        }}
                        pinnedBottomRowData={[pinnedBottomRow]}
                        suppressAggFuncInHeader={true}
                        getRowStyle={(params: any) => {
                            if (params.data?.isNewRow) {
                                return { background: '#f0f8ff' };
                            }
                        }}
                        domLayout={'autoHeight'}
                    />
                </div>
            </div>
        </div>
    );
};

export default TargetConfig;
